import { City } from "../city/city.model";
import { AngularFirestore } from "@angular/fire/firestore";
import { VenueBuilder } from "./venue.builder";
import { toAPIRequest, Venue } from "./venue.model";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../services";

export class VenueFactory {
  public venues$: Observable<Venue[]>;
  public builder: VenueBuilder;

  constructor(
    public city: City,
    public db: AngularFirestore,
    public httpClient: HttpClient,
    public authService: AuthService,
    public toast: ToastService
  ) {
    this.getVenuesOnInitialization();
  }

  private async getVenuesOnInitialization(): Promise<void> {
    // VenueFactory can be initialized with a specific city or a null city to indicate
    // that endpoint should not be scoped to an individual city.
    const getVenueUrl = this.city ? 
      `${environment.apiBaseUrl}/api/v1/venues/city/${this.city.reference.id}` :
      `${environment.apiBaseUrl}/api/v1/venues/`;

    const userJWT = await this.authService.user.getIdToken();
  
    this.venues$ = this.httpClient.get<Venue[]>(getVenueUrl, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    }).pipe(
      map(venuesResponse => venuesResponse
        .map((venueResponse) => new VenueBuilder().loadAPIResponse(venueResponse, this.db).build()))
    );
  }

  // getAllVenues will query the backend to get a list of all venues without any scoping
  // or filtering.
  public async getAllVenues(): Promise<Venue[]> {
    const userJWT = await this.authService.user.getIdToken();
    return this.httpClient.get<Venue[]>(`${environment.apiBaseUrl}/api/v1/venues`, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then(venues => 
        venues.map(venue => new VenueBuilder().loadAPIResponse(venue, this.db).build())
      );
  }

  // Create will create a new, empty venue locally via the VenueBuilder.
  public create = () => {
    this.builder = new VenueBuilder();
  }

  // Toggle will toggle the isActive property of the given venue both on the
  // frontend and backend. 
  public async toggle(venue: Venue) {
    const toggledVenue = new VenueBuilder()
      .edit({
        ...venue,
        isActive: !venue.isActive,
      } as Venue)
      .build();
    
    await this._update(toggledVenue);
  }

  // Fix updates the state of the locally stored venue by recreating the underlying
  // VenueBuilder.
  public fix = (venue: Venue) => {
    this.builder = new VenueBuilder().edit(venue)
  };

  // saveVenue will update the provided venue with the backend and refresh the
  // front-end state.
  public async saveVenue(venue: Venue): Promise<Venue> {
    return this._update(venue);
  }

  // addVenue will create the provided venue with the backend and refresh the
  // front-end state.
  public async addVenue(venue: Venue) {
    await this._add(venue);
  }

  private async _update(venue: Venue): Promise<Venue> {
    const userJWT = await this.authService.user.getIdToken();
    return this.httpClient.put(`${environment.apiBaseUrl}/api/v1/venues`, {
      id: venue.reference.id,
      data: {
        ...toAPIRequest(venue),
      },
    }, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then((updateVenueResponse) => {
        const updatedVenue = new VenueBuilder().loadAPIResponse(updateVenueResponse as Venue, this.db).build();

        this.venues$.pipe(
          map((venues) => venues.map((venue => {
            if (venue.id === updatedVenue.id) {
              return updatedVenue;
            }

            return venue;
          })))
        );

        return updatedVenue;
      })
      .catch((error) => {
        this.toast.error(
          `Failed to update ${venue.name}. Please try again.`,
          "Error",
        )
        
        throw(error);
      });
  }

  private async _add(venue: Venue) {
    const userJWT = await this.authService.user.getIdToken();
    this.httpClient.post(`${environment.apiBaseUrl}/api/v1/venues`, {
      ...toAPIRequest(venue),
    }, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then((createdVenue) => {
        this.venues$.pipe(
          map(venues => venues.concat(
            new VenueBuilder().loadAPIResponse(createdVenue as Venue, this.db).build())
          )
        )
      })
      .catch((error) => {
        this.toast.error(
          `Failed to update ${venue.name}. Please try again.`,
          "Error",
        )
      });
  }

  // refresh will return a promise that will query the backend and resolve to the venue
  // that corresponds to the given venueId.
  public async refresh(venueId: string): Promise<Venue> {
    const userJWT = await this.authService.user.getIdToken();
    return this.httpClient.get<Venue>(`${environment.apiBaseUrl}/api/v1/venues/${venueId}`, {
      headers: new HttpHeaders().set('Authorization', `${userJWT}`),
    })
      .toPromise()
      .then(venueResponse => new VenueBuilder().loadAPIResponse(venueResponse, this.db).build());
  }
}
